/**
 * @Author: v_jppjpeng
 * @Date: 2023-12-26 16:29:18
 * @Explain: 自定义 - 下拉框
 */

import { Icon, Input, Link, Tooltip } from '@/components/tdesign';
import { intlGet } from '@/locales';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { InputProps, TagInputProps } from 'tdesign-react';
import { Loading, Tag, TagInput } from 'tdesign-react';
// utils
import {
  isArray,
  isArrayLen,
  isEffectiveValue,
  isObject,
} from '@/utils/common/dataJudgment';
// type
import type { SelectBseProps } from '../data.d';
import { DisplayTextTypeEnum } from '../data.d';

type PropsType = {
  valueText?: any;
  visible?: boolean;
  searchPlaceholder?: string;
  /** 自动聚焦 */
  autofocus?: boolean;
  onSearch?: (val: string) => void;
  onTagRemove?: TagInputProps['onRemove'];
  label?: ReactNode | string;
  width?: number;
} & SelectBseProps;

function SelectCustom(props: PropsType) {
  const {
    className,
    visible,
    valueText,
    value,
    placeholder = intlGet('COMMON.PLEASE.SELECT'),
    loading,
    disabled,
    readonly = true,
    clearable,
    multiple,
    size,
    borderless = false,
    autoWidth,
    suspendedText,
    isDisplayTextStyle = false,
    displayText,
    displayTextKey,
    displayTextType = DisplayTextTypeEnum.TEXT,
    prefixIcon,
    suffixIconName = 'select-down',
    // tag - input - props
    minCollapsedNum,
    collapsedItems,
    valueDisplay,
    collapsedToolTipProps,
    searchPlaceholder,
    autofocus = false,
    onSearch,
    onClick,
    onClearable,
    onTagRemove,
    label,
  } = props;

  // displayTextType为'autoComplete'时搜索框内容
  const [inputSearchValue, setInputSearchValue] = useState<string>(value);

  const isAutoComplete = useMemo(
    () => displayTextType === DisplayTextTypeEnum.AUTO_COMPLETE,
    [displayTextType],
  );

  /** 当loading时 */
  const loadingRender = useCallback(
    (val: SelectBseProps['value']) => {
      return loading ? '' : val;
    },
    [loading],
  );

  /** 标签式 展示 */
  const renderCollapsedItems = ({ collapsedTags }: any) => (
    <Tooltip
      overlayClassName="select-tooltip-collapsed-tag"
      theme="light"
      placementOffset={[5, 0]}
      content={collapsedTags.map((item: any) => (
        <Tag size="large" key={item} className="mb-8 mr-8">
          {item}
        </Tag>
      ))}
      {...collapsedToolTipProps}
    >
      <Tag size="large">+{collapsedTags?.length}</Tag>
    </Tooltip>
  );

  // 文本展示 - map
  const displayTextMap = useMemo(
    () =>
      (isArray(valueText) &&
        valueText?.map((item: any) => {
          let val = item?.label;
          if (displayTextKey && item[displayTextKey]) {
            val = item[displayTextKey];
          }
          return val;
        })) ||
      [],
    [valueText, displayTextKey],
  );

  // 根据不同的情况，是否开启 display-text 样式
  const displayTextClass = useMemo(() => {
    if (isAutoComplete) {
      return false;
    }
    if (isArray(value)) {
      if (isArrayLen(value)) {
        return true;
      }
    } else if (value) {
      if (displayTextType === DisplayTextTypeEnum.CUSTOM_TEXT || autoWidth) {
        return true;
      }
    } else if (isDisplayTextStyle) {
      return true;
    }
    return false;
  }, [value, displayTextType]);

  // 公共配置
  const commonProps = useMemo(() => {
    const config: InputProps = {
      className: classNames(
        'default-td-select-box',
        {
          borderless,
          'is-focused': visible,
          't-tag-input': multiple,
          'display-text': displayTextClass,
          [`size-${size}`]: !autoWidth && size,
        },
        className,
      ),
      readonly: isAutoComplete ? false : !displayTextClass,
      disabled,
      autoWidth,
      clearable,
      placeholder: isAutoComplete ? searchPlaceholder : placeholder,
      suffixIcon: isAutoComplete ? undefined : loading ? (
        <Loading size="small" />
      ) : (
        <Link className="link-icon">
          <Icon.Font name={suffixIconName} fontSize={24} />
        </Link>
      ),
      onClick,
      onClear: ({ e }) => {
        onClearable?.();
        e.stopPropagation();
      },
    };

    if (prefixIcon) {
      config.prefixIcon = prefixIcon;
    }

    return config as any;
  }, [
    className,
    displayTextClass,
    visible,
    value,
    size,
    multiple,
    readonly,
    disabled,
    loading,
    clearable,
    autoWidth,
    displayTextKey,
    prefixIcon,
    suffixIconName,
    isAutoComplete,
  ]);

  const inputProps = useMemo(() => {
    const title = suspendedText
      ? suspendedText
      : valueText?.[0]?.label || displayText;
    const config: InputProps & { title: string } = {
      autofocus,
      value: isAutoComplete ? inputSearchValue : loadingRender(value) || '',
      title,
      onChange: (val) => {
        setInputSearchValue(val);
        onSearch?.(val);
      },
    };
    let labelText: any = displayText;

    if (displayTextType !== DisplayTextTypeEnum.DISPLAY_TEXT) {
      if (
        isEffectiveValue(value) &&
        displayTextMap &&
        isArrayLen(displayTextMap)
      ) {
        labelText = displayTextMap[0];
      }
    }

    if (isObject(labelText) || autoWidth || displayTextClass) {
      config.prefixIcon = loadingRender(labelText || value);
    } else if (labelText) {
      config.value = labelText;
    }

    return config;
  }, [
    loading,
    valueText,
    displayTextClass,
    value,
    displayTextMap,
    autoWidth,
    displayText,
    displayTextKey,
    displayTextType,
    inputSearchValue,
    autofocus,
    isAutoComplete,
    suspendedText,
    loadingRender,
  ]);

  const tagInputProps = useMemo(() => {
    const config: TagInputProps = {
      value: value || [],
      size: 'large',
      minCollapsedNum,
      collapsedItems: collapsedItems || renderCollapsedItems,
      valueDisplay,
      onRemove: (context) => {
        onTagRemove?.(context);
        context.e?.stopPropagation();
      },
    };
    let labelText: TagInputProps['value'] = [];

    if (isArrayLen(value) && displayTextMap && isArrayLen(displayTextMap)) {
      labelText = displayTextMap;
    }

    config.value = labelText;

    return config;
  }, [
    value,
    displayTextMap,
    minCollapsedNum,
    collapsedItems,
    valueDisplay,
    renderCollapsedItems,
    onTagRemove,
  ]);

  return displayTextType === DisplayTextTypeEnum.TAG || multiple ? (
    <TagInput {...tagInputProps} {...commonProps} />
  ) : isAutoComplete ? (
    <Input {...inputProps} {...commonProps} autofocus />
  ) : (
    <Input {...inputProps} {...commonProps} label={label} />
  );
}

export default SelectCustom;
